import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ProgressBar, Spinner } from "react-bootstrap";
import { Localization, SessionContext, SettingsManager, AppMessagesContext, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { autoUpdate, flip, offset, shift, useClick, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { floatingBoundaryMiddleware, MITFloatingPortal, useFloatingPortalContext } from "src/componentsUtils/PortalHelpers";
import { ConfirmationDialog } from "src/components/ConfirmationDialog";
import { actionSetMapInteractionState, MapInteractionState, MapitStateContext } from "src/states/MapitState";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { closeWindow, ManagedWindow, openWindow, sendParams, WindowId, WindowState } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { LayerInfo, LayerType } from "src/common/managers/Types";
import { OptimisticLockingConflictError } from "src/common/managers/ESVersionedPersistance";
import { PLADataServiceImpl, PLAProjectServiceImpl } from "./PLAPersistence";
import { PLAElementRecord, PLAProject, PLARecord, PLARelationRecord, PLALocalStorageSelectedProjectKey, PLAProjectsChangedEvent, PLAElementDocumentsChangedEvent, getMyPLAAccessRights, hasPLAWriteAccess, hasPLAAdminAccess, getPLAVisibleElementTypes, isPLANumberValueType } from "./PLAState";
import { evaluatePLAExpression, getPLAAlerts, resolvePLAElementExpressionAttributes, validatePLAData, validatePLAExpression } from "./PLAExpression";
import { buildPLAZoomFeature, removePLAMapLayers, setPLASelectedFeature, setupPLAMapLayers } from "./PLAMapLayers";
import { PLAElementDialog, createEmptyPLAElement, PLARelationDialog } from "./PLAElementDialog";
import { PLALLMCheckDialog } from "./PLALLMCheckDialog";
import type { PLALLMCheckApplyChange } from "./PLALLMCheck";
import { PLAAlertMark } from "./PLAAlertMark";
import { PLATable, PLATableRef } from "./PLATable";
import { PLAElementTreeDiagram } from "./PLAElementTreeDiagram";
import { createPLAFreeAreaReport } from "./PLAReport";
import { createPLAOptimizationResultAsync, PLAOptimizationResult, PLAOptimizerAlgorithm, PLAOptimizerGoal, PLAOptimizerProposal } from "./PLAOptimizer";
import { formatPLANumber } from "./PLAFormatting";
import { PLAExpressionInput } from "./PLAExpressionInput";
import { PLAExpressionSimulationDialog } from "./PLAExpressionSimulationDialog";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { arePLAElementTransformValuesEqual, getPLAElementTransformStoredValue, getPLAElementTransformValueError } from "./PLAElementTransform";
import type { PLAAttributeUsageWindowParams } from "./PLAAttributeUsageWindow";
import { cleanupPLAStaleRelations } from "./PLARelationCleanup";
import { PLACubeViewContext } from "./PLACubeViewContext";
import { changePLACubeTimeDimension, getPLACubePhaseElements, normalizePLACubeViewSettings, PLACubeViewSettings } from "./PLACubeTimeView";
import { PLACubeGlobalFilterDialog } from "./PLACubeGlobalFilterDialog";
import { PLAFilterButton } from "./PLAFilterButton";
import { readPLACubeViewSettings, savePLACubeViewSettings } from "./PLACubeViewStorage";
import "./PLAWindow.css";

type ElementTypeMapSelectionMode = "single" | "multiple";
type OptimizerTargetOption = { value: string; text: string };
type PLAElementTransformResult = { processed: number; changed: number; unchanged: number; errors: string[] };

const PLALocalStorageMapSelectionModeKey = "PLAState-mapElementTypeSelectionMode";
const PLALocalStorageShowPolygonNamesKey = "PLAState-showPolygonNames";
const PLALocalStorageShowFormulaeKey = "PLAState-showFormulae";
const PLALocalStorageVisibleElementTypesKey = (projectId: string) => `PLAState-visibleElementTypes-${projectId}`;
const PLALocalStorageSelectedElementTypeKey = (projectId: string) => `PLAState-selectedElementType-${projectId}`;
const PLALocalStorageOptimizerTargetAttributeKey = (projectId: string) => `PLAState-optimizerTargetAttribute-${projectId}`;
const PLALocalStorageOptimizerGoalKey = "PLAState-optimizerGoal";
const PLALocalStorageOptimizerAlgorithmKey = "PLAState-optimizerAlgorithm";
const PLAExternalRefreshIntervalMilliseconds = 1000;
const PLAMolbakLogo = {
   src: "/Moelbaklogo-Groen-Stort.png",
   alt: "Mølbak",
   href: "https://molbak.dk/",
   stacked: false
};
const PLAJernbanebyenLogo = {
   src: "/customerLogo/Jernbanebyen.svg",
   alt: "Jernbanebyen",
   href: "https://jernbanebyen.dk/",
   stacked: true
};

function isJernbanebyenProject(project?: PLAProject) {
   return (project?.name || "").trim().toLowerCase().includes("jernbanebyen");
}

function PLAReportsMenu(props: { label: string; items: { value: string; text: string; disabled?: boolean }[]; onSelect: (value: string) => void }) {
   const { root } = useFloatingPortalContext();
   const [open, setOpen] = useState(false);
   const { refs, floatingStyles, context } = useFloating({
      whileElementsMounted: autoUpdate,
      open,
      onOpenChange: setOpen,
      placement: "bottom-end",
      strategy: "fixed",
      middleware: [
         offset(6),
         ...(root != null ? floatingBoundaryMiddleware(root) : [flip({ padding: 8 }), shift({ padding: 8 })]),
      ],
   });
   const { getReferenceProps, getFloatingProps } = useInteractions([
      useClick(context),
      useDismiss(context),
   ]);

   return (
      <>
         <button
            type="button"
            className="pla-btn pla-btn--solid pla-btn--menu"
            ref={refs.setReference}
            aria-haspopup="menu"
            aria-expanded={open}
            {...getReferenceProps()}
         >
            {Localization.getText(props.label)}
         </button>
         {open ? (
            <MITFloatingPortal>
               <div
                  ref={refs.setFloating}
                  className="pla-menu__panel"
                  role="menu"
                  style={floatingStyles}
                  {...getFloatingProps()}
               >
                  {props.items.map((item) => (
                     <button
                        key={item.value}
                        type="button"
                        role="menuitem"
                        disabled={item.disabled}
                        onClick={() => {
                           if (item.disabled) return;
                           setOpen(false);
                           props.onSelect(item.value);
                        }}
                     >
                        {item.text}
                     </button>
                  ))}
               </div>
            </MITFloatingPortal>
         ) : null}
      </>
   );
}

function getStoredBoolean(key: string, defaultValue: boolean) {
   const value = localStorage.getItem(key);
   if (value === "true") return true;
   if (value === "false") return false;
   return defaultValue;
}

function getStoredOptimizerGoal(): PLAOptimizerGoal {
   return localStorage.getItem(PLALocalStorageOptimizerGoalKey) === "maximize" ? "maximize" : "minimize";
}

function getStoredOptimizerAlgorithm(): PLAOptimizerAlgorithm {
   return localStorage.getItem(PLALocalStorageOptimizerAlgorithmKey) === "astar" ? "astar" : "greedy";
}

function getSortedElementTypeIds(project: PLAProject) {
   return Object.entries(project.elementTypes || {}).sort((a, b) => a[1].sortOrder - b[1].sortOrder).map(([elementTypeId]) => elementTypeId);
}

function allElementTypesVisible(project: PLAProject) {
   return getSortedElementTypeIds(project).reduce<Record<string, boolean>>((acc, elementTypeId) => {
      acc[elementTypeId] = true;
      return acc;
   }, {});
}

function singleElementTypeVisible(project: PLAProject, selectedElementTypeId: string | undefined) {
   return getSortedElementTypeIds(project).reduce<Record<string, boolean>>((acc, elementTypeId) => {
      acc[elementTypeId] = elementTypeId === selectedElementTypeId;
      return acc;
   }, {});
}

function normalizeVisibleElementTypes(project: PLAProject, value: any) {
   if (!value || typeof value !== "object") return allElementTypesVisible(project);
   return getSortedElementTypeIds(project).reduce<Record<string, boolean>>((acc, elementTypeId) => {
      acc[elementTypeId] = value[elementTypeId] !== false;
      return acc;
   }, {});
}

function getLowestSortVisibleElementTypeId(project: PLAProject, visibleElementTypes: Record<string, boolean>) {
   return getSortedElementTypeIds(project).find((elementTypeId) => visibleElementTypes[elementTypeId] !== false);
}

function getStoredVisibleElementTypes(project: PLAProject) {
   try {
      return normalizeVisibleElementTypes(project, JSON.parse(localStorage.getItem(PLALocalStorageVisibleElementTypesKey(project.id)) || "null"));
   } catch {
      return allElementTypesVisible(project);
   }
}

function getStoredSelectedElementTypeId(project: PLAProject) {
   const stored = localStorage.getItem(PLALocalStorageSelectedElementTypeKey(project.id)) || undefined;
   return stored && project.elementTypes[stored] ? stored : undefined;
}

function getRecordsSignature(records: PLARecord[]) {
   return records
      .map((record) => `${record.id}:${record._version ?? JSON.stringify(record)}`)
      .sort()
      .join("|");
}

function getRecordSignature(record: PLARecord | undefined) {
   return record ? `${record.id}:${record._version ?? JSON.stringify(record)}` : "";
}

export function PLAWindow(props: { managedWindow: ManagedWindow }) {
   const { state: sessionState } = useContext(SessionContext);
   const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);

   const { state: windowState, dispatch: windowDispatch } = useWindowContext();
   const { root: floatingPortalRoot } = useFloatingPortalContext();
   const plaPortalRoot = props.managedWindow.asPopop ? floatingPortalRoot : document.getElementById("Mit-NonBlockingOverlay") || floatingPortalRoot;
   const [projects, setProjects] = useState<PLAProject[]>([]);
   const [selectedProjectId, setSelectedProjectId] = useState<string>(localStorage.getItem(PLALocalStorageSelectedProjectKey) || "");
   const [elements, setElements] = useState<PLAElementRecord[]>([]);
   const [relations, setRelations] = useState<PLARelationRecord[]>([]);
   const [loading, setLoading] = useState(false);
   const [activeTab, setActiveTab] = useState<string | undefined>();
   const [visibleElementTypes, setVisibleElementTypes] = useState<Record<string, boolean>>({});
   const [elementTypeMapSelectionMode, setElementTypeMapSelectionMode] = useState<ElementTypeMapSelectionMode>(() => localStorage.getItem(PLALocalStorageMapSelectionModeKey) === "single" ? "single" : "multiple");
   const [lastSelectedElementTypeId, setLastSelectedElementTypeId] = useState<string | undefined>();
   const [selectedElementId, setSelectedElementId] = useState<string | undefined>();
   const [showElementEditor, setShowElementEditor] = useState<PLAElementRecord | undefined>();
   const [showRelationEditor, setShowRelationEditor] = useState<PLAElementRecord | undefined>();
   const [confirmDeleteElement, setConfirmDeleteElement] = useState<PLAElementRecord | undefined>();
   const [confirmRelationCleanupProjectId, setConfirmRelationCleanupProjectId] = useState<string>();
   const [relationCleanupRunning, setRelationCleanupRunning] = useState(false);
   const [showMapSettings, setShowMapSettings] = useState(false);
   const [showGlobalCubeFilter, setShowGlobalCubeFilter] = useState(false);
   const [projectCubeViewSettings, setProjectCubeViewSettings] = useState<Record<string, PLACubeViewSettings>>({});
   const [showLlmCheck, setShowLlmCheck] = useState(false);
   const [showElementTransform, setShowElementTransform] = useState(false);
   const [transformExpression, setTransformExpression] = useState("");
   const [transformTargetAttributeId, setTransformTargetAttributeId] = useState("");
   const [transformExpressionEditorOpen, setTransformExpressionEditorOpen] = useState(false);
   const [transformRunning, setTransformRunning] = useState(false);
   const [transformProgress, setTransformProgress] = useState("");
   const [transformResult, setTransformResult] = useState<PLAElementTransformResult>();
   const [showOptimizerParameters, setShowOptimizerParameters] = useState(false);
   const [optimizerResult, setOptimizerResult] = useState<PLAOptimizationResult | undefined>();
   const [optimizerRunning, setOptimizerRunning] = useState(false);
   const [optimizerProgress, setOptimizerProgress] = useState("");
   const [optimizerProgressPercent, setOptimizerProgressPercent] = useState(0);
   const [optimizerTargetAttributeId, setOptimizerTargetAttributeId] = useState("");
   const [optimizerGoal, setOptimizerGoal] = useState<PLAOptimizerGoal>(() => getStoredOptimizerGoal());
   const [optimizerAlgorithm, setOptimizerAlgorithm] = useState<PLAOptimizerAlgorithm>(() => getStoredOptimizerAlgorithm());
   const [showPolygonNames, setShowPolygonNames] = useState(() => getStoredBoolean(PLALocalStorageShowPolygonNamesKey, true));
   const [showFormulae, setShowFormulae] = useState(() => getStoredBoolean(PLALocalStorageShowFormulaeKey, false));
   const persistedElementIds = useRef<Set<string>>(new Set());
   const persistedRelationIds = useRef<Set<string>>(new Set());
   const refreshInProgress = useRef(false);
   const localPersistenceInProgress = useRef(false);
   const loadedRecordsSignature = useRef("");
   const loadedProjectId = useRef<string | undefined>();
   const tableRefs = useRef<Record<string, PLATableRef | null>>({});
   const selectedProject = projects.find((project) => project.id === selectedProjectId);
   const administrator = hasPLAAdminAccess(selectedProject, sessionState.userRef, sessionState.customerRef) || hasAccessToFeature(Feature.PLAToolGlobalAdmin);
   const sortedElementTypes = useMemo(() => getPLAVisibleElementTypes(selectedProject, administrator), [selectedProject, administrator]);
   const visibleActiveTab = sortedElementTypes.some(([id]) => id === activeTab) ? activeTab : sortedElementTypes[0]?.[0];
   const configuredTimeDimensionId = selectedProject?.cubeTimeDimensionId || "";
   const storedCubeViewSettings = useMemo(() => {
      const settings = projectCubeViewSettings[selectedProjectId] ?? readPLACubeViewSettings(localStorage, selectedProjectId, configuredTimeDimensionId);
      return settings.dimensionId !== configuredTimeDimensionId
         ? changePLACubeTimeDimension(settings, configuredTimeDimensionId)
         : settings;
   }, [configuredTimeDimensionId, projectCubeViewSettings, selectedProjectId]);
   const phaseDimensionId = configuredTimeDimensionId && selectedProject?.elementTypes[configuredTimeDimensionId] ? configuredTimeDimensionId : undefined;
   const phases = useMemo(() => phaseDimensionId ? getPLACubePhaseElements(elements, phaseDimensionId) : [], [elements, phaseDimensionId]);
   const cubeViewSettings = useMemo<PLACubeViewSettings>(() => normalizePLACubeViewSettings(
      storedCubeViewSettings, phaseDimensionId, phases
   ), [storedCubeViewSettings, phaseDimensionId, phases]);
   const persistCubeViewSettings = (settings: PLACubeViewSettings) => {
      try {
         savePLACubeViewSettings(localStorage, selectedProjectId, settings);
      } catch (error) {
         appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to save PLA settings")}: ${error instanceof Error ? error.message : String(error)}`));
         return;
      }
      setProjectCubeViewSettings((previous) => ({ ...previous, [selectedProjectId]: settings }));
   };
   const changeCubeViewSettings = (settings: PLACubeViewSettings) => persistCubeViewSettings({
      ...settings, resetVersion: (storedCubeViewSettings.resetVersion || 0) + (cubeViewSettings.memberId && !settings.memberId ? 1 : 0)
   });
   const transformElementTypeId = visibleActiveTab;
   const transformElementType = transformElementTypeId ? selectedProject?.elementTypes[transformElementTypeId] : undefined;
   const transformTargetOptions = useMemo(() => Object.entries(transformElementType?.attributes || {})
      .filter(([, definition]) => definition.type !== "expression" && Boolean(definition.valueType))
      .sort(([, a], [, b]) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER))
      .map(([attributeId, definition]) => ({ value: attributeId, text: `${definition.name || attributeId} (${definition.type}, ${definition.valueType})` })), [transformElementType]);
   const mapFacade = mapitState.map;

   const availableLayers = useMemo(() => Object.values(mapitState.layers || {})
      .filter((layer: LayerInfo) => [LayerType.GeoJSON_Point, LayerType.GeoJSON_Polygon, LayerType.GeoJSON].includes(layer.type))
      .sort((a: LayerInfo, b: LayerInfo) => String(a.datasetname || a.filename || a.layerId).localeCompare(String(b.datasetname || b.filename || b.layerId))), [mapitState.layers]);
   const writable = hasPLAWriteAccess(selectedProject, sessionState.userRef, sessionState.customerRef);
   const readOnly = !writable;
   const mapElements = useMemo(() => elements.filter((element) => visibleElementTypes[element.elementType] !== false), [elements, visibleElementTypes]);
   const optimizerTargetOptions = useMemo<OptimizerTargetOption[]>(() => {
      if (!selectedProject) return [];
      const seen = new Set<string>();
      return Object.entries(selectedProject.elementTypes || {}).filter(([, candidateElementType]) => candidateElementType.sortOrder === 1).reduce<OptimizerTargetOption[]>((options, [elementTypeId, projectElementType]) => {
         Object.entries(projectElementType?.attributes || {}).forEach(([attributeId, definition]) => {
            if (!isPLANumberValueType(definition.valueType) || seen.has(attributeId)) return;
            seen.add(attributeId);
            options.push({ value: attributeId, text: `${projectElementType?.name || elementTypeId}: ${definition.name || attributeId}` });
         });
         return options;
      }, []);
   }, [selectedProject]);
   const defaultOptimizerTargetAttributeId = optimizerTargetOptions.find((option) => option.value === "VærdiAfUudnyttedeByggeretter")?.value || optimizerTargetOptions[0]?.value || "";

   const projectService = () => new PLAProjectServiceImpl(SettingsManager.getSystemSetting("pla.projectsElasticIndex", ""), sessionState.userName || sessionState.userRef);
   const dataService = (index: string) => new PLADataServiceImpl(index, sessionState.userName || sessionState.userRef);

   const resolveElementsForRecords = useCallback((nextElements: PLAElementRecord[], nextRelations: PLARelationRecord[]) => {
      if (!selectedProject) return nextElements;
      return nextElements.map((element) => resolvePLAElementExpressionAttributes(element, selectedProject, nextElements, nextRelations, { includeErrors: true }));
   }, [selectedProject]);

   const applyRemoteRecords = useCallback((records: PLARecord[]) => {
      if (!selectedProject) return;
      const nextSignature = getRecordsSignature(records);
      if (nextSignature === loadedRecordsSignature.current) return;
      loadedRecordsSignature.current = nextSignature;
      const nextElements = records.filter((record): record is PLAElementRecord => record.recordType === "ELEMENT");
      const nextRelations = records.filter((record): record is PLARelationRecord => record.recordType === "RELATION");
      const resolvedElements = resolveElementsForRecords(nextElements, nextRelations);
      setElements(resolvedElements);
      setRelations(nextRelations);
      persistedElementIds.current = new Set(resolvedElements.map((element) => element.id));
      persistedRelationIds.current = new Set(nextRelations.map((relation) => relation.id));
      setShowElementEditor((current) => {
         const refreshedElement = current ? resolvedElements.find((element) => element.id === current.id) : undefined;
         return refreshedElement && getRecordSignature(refreshedElement) !== getRecordSignature(current) ? refreshedElement : current;
      });
      setShowRelationEditor((current) => {
         const refreshedElement = current ? resolvedElements.find((element) => element.id === current.id) : undefined;
         return refreshedElement && getRecordSignature(refreshedElement) !== getRecordSignature(current) ? refreshedElement : current;
      });
      setConfirmDeleteElement((current) => {
         const refreshedElement = current ? resolvedElements.find((element) => element.id === current.id) : undefined;
         return refreshedElement && getRecordSignature(refreshedElement) !== getRecordSignature(current) ? refreshedElement : current;
      });
   }, [selectedProject, resolveElementsForRecords]);

   const applyRemoteRecord = useCallback((record: PLARecord) => {
      const nextElements = record.recordType === "ELEMENT"
         ? (elements.some((element) => element.id === record.id) ? elements.map((element) => element.id === record.id ? record : element) : [...elements, record])
         : elements;
      const nextRelations = record.recordType === "RELATION"
         ? (relations.some((relation) => relation.id === record.id) ? relations.map((relation) => relation.id === record.id ? record : relation) : [...relations, record])
         : relations;
      applyRemoteRecords([...nextElements, ...nextRelations]);
   }, [applyRemoteRecords, elements, relations]);

   const loadProjectRecords = useCallback(async () => {
      if (!selectedProject) return;
      const records = await dataService(selectedProject.esIndexName).loadRecords();
      applyRemoteRecords(records);
   }, [applyRemoteRecords, selectedProject?.esIndexName, sessionState.userName, sessionState.userRef]);

   const loadProjects = useCallback(() => {
      projectService().loadProjects()
         .then((loadedProjects) => {
            const visibleProjects = loadedProjects.filter((project) => {
               if (hasAccessToFeature(Feature.PLAToolGlobalAdmin)) return true;
               return getMyPLAAccessRights(project, sessionState.userRef, sessionState.customerRef).length > 0;
            });
            setProjects(visibleProjects);
            if (!selectedProjectId && visibleProjects[0]) setSelectedProjectId(visibleProjects[0].id);
         })
         .catch((error) => appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to load projects")}: ${error.message || error}`)));
   }, [appMessageDispatch, hasAccessToFeature, selectedProjectId, sessionState.customerRef, sessionState.userName, sessionState.userRef]);

   useEffect(() => {
      mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Override));

      return () => {
         mapitStateDispatch(actionSetMapInteractionState(MapInteractionState.Normal));
      };
   }, []);

   useEffect(() => {
      loadProjects();
   }, [loadProjects]);

   useEffect(() => {
      globalThis.addEventListener?.(PLAProjectsChangedEvent, loadProjects);
      return () => globalThis.removeEventListener?.(PLAProjectsChangedEvent, loadProjects);
   }, [loadProjects]);

   useEffect(() => {
      const refreshDocumentState = (event: Event) => {
         const projectId = (event as CustomEvent<{ projectId?: string }>).detail?.projectId;
         if (projectId !== selectedProject?.id) return;
         loadProjectRecords().catch((error) => appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to refresh project data")}: ${error.message || error}`)));
      };
      globalThis.addEventListener?.(PLAElementDocumentsChangedEvent, refreshDocumentState);
      return () => globalThis.removeEventListener?.(PLAElementDocumentsChangedEvent, refreshDocumentState);
   }, [appMessageDispatch, loadProjectRecords, selectedProject?.id]);

   useEffect(() => {
      if (!selectedProject) return;
      localStorage.setItem(PLALocalStorageSelectedProjectKey, selectedProject.id);
      setLoading(true);
      dataService(selectedProject.esIndexName).loadRecords()
         .then((records) => {
            const isSameProjectReload = loadedProjectId.current === selectedProject.id;
            loadedProjectId.current = selectedProject.id;
            applyRemoteRecords(records);
            const firstType = getSortedElementTypeIds(selectedProject)[0];
            const storedVisibleElementTypes = getStoredVisibleElementTypes(selectedProject);
            const storedSelectedElementTypeId = getStoredSelectedElementTypeId(selectedProject);
            const selectedElementTypeId = storedSelectedElementTypeId || getLowestSortVisibleElementTypeId(selectedProject, storedVisibleElementTypes) || firstType;
            setActiveTab((current) => isSameProjectReload && current && selectedProject.elementTypes?.[current] ? current : selectedElementTypeId);
            setLastSelectedElementTypeId((current) => isSameProjectReload && current && selectedProject.elementTypes?.[current] ? current : selectedElementTypeId);
            setVisibleElementTypes((current) => isSameProjectReload && Object.keys(current).length > 0 ? current : elementTypeMapSelectionMode === "single" ? singleElementTypeVisible(selectedProject, selectedElementTypeId) : storedVisibleElementTypes);
         })
         .catch((error) => appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to load project data")}: ${error.message || error}`)))
         .finally(() => setLoading(false));
   }, [applyRemoteRecords, selectedProject?.id]);

   useEffect(() => {
      if (!selectedProject) return;
      const refresh = () => {
         if (refreshInProgress.current || localPersistenceInProgress.current) return;
         refreshInProgress.current = true;
         loadProjectRecords()
            .catch(() => undefined)
            .finally(() => { refreshInProgress.current = false; });
      };
      const intervalId = window.setInterval(refresh, PLAExternalRefreshIntervalMilliseconds);
      return () => window.clearInterval(intervalId);
   }, [loadProjectRecords, selectedProject?.id]);

   useEffect(() => {
      localStorage.setItem(PLALocalStorageMapSelectionModeKey, elementTypeMapSelectionMode);
   }, [elementTypeMapSelectionMode]);

   useEffect(() => {
      localStorage.setItem(PLALocalStorageShowPolygonNamesKey, String(showPolygonNames));
   }, [showPolygonNames]);

   useEffect(() => {
      localStorage.setItem(PLALocalStorageShowFormulaeKey, String(showFormulae));
   }, [showFormulae]);

   useEffect(() => {
      if (!selectedProject) return;
      localStorage.setItem(PLALocalStorageVisibleElementTypesKey(selectedProject.id), JSON.stringify(visibleElementTypes));
   }, [selectedProject?.id, visibleElementTypes]);

   useEffect(() => {
      if (!selectedProject || !lastSelectedElementTypeId) return;
      localStorage.setItem(PLALocalStorageSelectedElementTypeKey(selectedProject.id), lastSelectedElementTypeId);
   }, [selectedProject?.id, lastSelectedElementTypeId]);

   useEffect(() => {
      if (!selectedProject) return;
      const storedTargetAttributeId = localStorage.getItem(PLALocalStorageOptimizerTargetAttributeKey(selectedProject.id)) || "";
      const validStoredTargetAttributeId = optimizerTargetOptions.some((option) => option.value === storedTargetAttributeId) ? storedTargetAttributeId : "";
      setOptimizerTargetAttributeId(validStoredTargetAttributeId || defaultOptimizerTargetAttributeId);
   }, [selectedProject?.id, defaultOptimizerTargetAttributeId, optimizerTargetOptions]);

   useEffect(() => {
      localStorage.setItem(PLALocalStorageOptimizerGoalKey, optimizerGoal);
   }, [optimizerGoal]);

   useEffect(() => {
      localStorage.setItem(PLALocalStorageOptimizerAlgorithmKey, optimizerAlgorithm);
   }, [optimizerAlgorithm]);

   useEffect(() => {
      if (!selectedProject || !optimizerTargetAttributeId) return;
      localStorage.setItem(PLALocalStorageOptimizerTargetAttributeKey(selectedProject.id), optimizerTargetAttributeId);
   }, [selectedProject?.id, optimizerTargetAttributeId]);

   const alertsByElementId = useMemo(() => {
      if (!selectedProject) return {};
      return elements.reduce<Record<string, string[]>>((acc, element) => {
         acc[element.id] = getPLAAlerts(element, selectedProject, elements, relations);
         return acc;
      }, {});
   }, [selectedProject, elements, relations]);

   const alertMessagesByElementType = useMemo(() => {
      return elements.reduce<Record<string, string[]>>((acc, element) => {
         const messages = (alertsByElementId[element.id] || []).map((message) => element.name ? `${element.name}: ${message}` : message);
         if (!messages.length) return acc;
         acc[element.elementType] = [...(acc[element.elementType] || []), ...messages];
         return acc;
      }, {});
   }, [elements, alertsByElementId]);

   useEffect(() => {
      const map = mapFacade?.getMapPop?.();
      if (!map || !selectedProject) return;
      const cleanup = setupPLAMapLayers(map, mapElements, alertsByElementId, {
         onFeatureClick: (elementId) => {
            const element = elements.find((item) => item.id === elementId);
            if (!element) return;
            setPLASelectedFeature(map, elementId);
            setSelectedElementId(elementId);
            setActiveTab(element.elementType);
            window.setTimeout(() => tableRefs.current[element.elementType]?.scrollToRow(elementId, true), 0);
         }
      }, selectedProject.elementTypes || {}, { showPolygonLabels: showPolygonNames });
      return cleanup;
   }, [mapFacade, elements, mapElements, alertsByElementId, selectedProject, showPolygonNames]);

   useEffect(() => {
      return () => removePLAMapLayers(mapFacade?.getMapPop?.());
   }, [mapFacade]);

   const zoomToElement = useCallback((element: PLAElementRecord) => {
      setSelectedElementId(element.id);
      const map = mapFacade?.getMapPop?.();
      if (map) setPLASelectedFeature(map, element.id);
      const feature = buildPLAZoomFeature(element);
      if (feature) mapFacade?.zoomToDataAvoidingWindows?.(feature, 25);
   }, [mapFacade]);

   const navigateToElement = useCallback((elementId: string) => {
      const element = elements.find((item) => item.id === elementId);
      if (!element) return;
      setActiveTab(element.elementType);
      zoomToElement(element);
      window.setTimeout(() => tableRefs.current[element.elementType]?.scrollToRow(elementId, true), 0);
   }, [elements, zoomToElement]);

   const elementTreeWindowParams = useMemo<PLAElementTreeDiagramWindowParams | undefined>(() => {
      if (!selectedProject) return undefined;
      return { project: selectedProject, elements, relations, onElementClick: navigateToElement };
   }, [selectedProject, elements, relations, navigateToElement]);

   const elementTreeWindow = windowState.find((item) => item.id === WindowId.PLAElementTreeDiagram);
   const attributeUsageWindow = windowState.find((item) => item.id === WindowId.PLAAttributeUsage);
   const attributeUsageWindowParams = useMemo<PLAAttributeUsageWindowParams | undefined>(() =>
      selectedProject ? { project: selectedProject, elements } : undefined, [selectedProject, elements]);

   useEffect(() => {
      if (attributeUsageWindow?.state !== WindowState.Open || !attributeUsageWindowParams) return;
      windowDispatch(sendParams(WindowId.PLAAttributeUsage, attributeUsageWindowParams));
   }, [attributeUsageWindow?.state, attributeUsageWindowParams, windowDispatch]);

   useEffect(() => {
      if (elementTreeWindow?.state !== WindowState.Open || !elementTreeWindowParams) return;
      windowDispatch(sendParams(WindowId.PLAElementTreeDiagram, elementTreeWindowParams));
   }, [elementTreeWindow?.state, elementTreeWindowParams, windowDispatch]);

   const showSaveError = async (error: any) => {
      appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to save changes")}: ${error.message || error}`));
      if (error instanceof OptimisticLockingConflictError) {
         const record = error.currentDocument as PLARecord | undefined;
         if (record?.recordType === "ELEMENT" || record?.recordType === "RELATION") applyRemoteRecord(record);
         await loadProjectRecords().catch(() => undefined);
      }
   };

   const resolveElementsForState = (nextElements: PLAElementRecord[], nextRelations: PLARelationRecord[]) => {
      return resolveElementsForRecords(nextElements, nextRelations);
   };

   const validateNextState = (nextElements: PLAElementRecord[], nextRelations: PLARelationRecord[]) => {
      if (!selectedProject) return false;
      const validationErrors = validatePLAData(selectedProject, nextElements, nextRelations, { ignoreExpressionErrors: true });
      if (validationErrors.length > 0) {
         appMessageDispatch(actionSetErrorMessage(validationErrors.join("\n")));
         return false;
      }
      return true;
   };

   const persistElementChanges = async (nextElements: PLAElementRecord[], nextRelations: PLARelationRecord[], elementIdsToSave: string[]) => {
      if (readOnly) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Editing elements requires write access")));
         return false;
      }
      if (!selectedProject || !validateNextState(nextElements, nextRelations)) return false;
      const resolvedElements = resolveElementsForState(nextElements, nextRelations);
      const elementsToSave = resolvedElements.filter((element) => elementIdsToSave.includes(element.id));
      localPersistenceInProgress.current = true;
      try {
         const service = dataService(selectedProject.esIndexName);
         await Promise.all(elementsToSave.map((element) => service.saveElement(element)));
         setElements(resolvedElements);
         elementsToSave.forEach((element) => persistedElementIds.current.add(element.id));
         return true;
      } catch (error: any) {
         await showSaveError(error);
         return false;
      } finally {
         localPersistenceInProgress.current = false;
      }
   };

   const applyLlmCheckValues = async (changes: PLALLMCheckApplyChange[]) => {
      if (!selectedProject || !changes.length) return false;
      const changesByElementId = new Map<string, PLALLMCheckApplyChange[]>();
      changes.forEach((change) => {
         changesByElementId.set(change.elementId, [...(changesByElementId.get(change.elementId) || []), change]);
      });
      const nextElements = elements.map((element) => {
         const elementChanges = changesByElementId.get(element.id);
         if (!elementChanges) return element;
         const attributes = { ...element.attributes };
         elementChanges.forEach((change) => {
            attributes[change.attributeId] = { mode: "constant", valueType: change.valueType, value: change.value };
         });
         return { ...element, attributes };
      });
      const saved = await persistElementChanges(nextElements, relations, Array.from(changesByElementId.keys()));
      if (saved) appMessageDispatch(actionSetInfoMessage(Localization.getText("Applied LLM values")));
      return saved;
   };

   const runElementTransform = async () => {
      if (!selectedProject || !transformElementTypeId || !transformTargetAttributeId || transformRunning || readOnly) return;
      const definition = selectedProject.elementTypes[transformElementTypeId]?.attributes[transformTargetAttributeId];
      if (!definition || definition.type === "expression" || !definition.valueType) return;
      const expression = transformExpression;
      const validation = validatePLAExpression(expression, { project: selectedProject, elementTypeId: transformElementTypeId });
      if (!expression.trim() || !validation.valid) {
         setTransformResult({ processed: 0, changed: 0, unchanged: 0, errors: [validation.error || Localization.getText("Expression is required")] });
         return;
      }

      const selectedElements = elements.filter((element) => element.elementType === transformElementTypeId);
      const updates = new Map<string, PLAElementRecord>();
      const errors: string[] = [];
      setTransformRunning(true);
      setTransformResult(undefined);
      setTransformProgress("");
      try {
         for (let index = 0; index < selectedElements.length; index++) {
            const element = selectedElements[index];
            try {
               const value = evaluatePLAExpression(expression, { element, elements, relations, project: selectedProject });
               const valueError = getPLAElementTransformValueError(value, definition);
               if (valueError) throw new Error(Localization.getText(valueError));
               const existing = element.attributes[transformTargetAttributeId];
               const storedValue = getPLAElementTransformStoredValue(value, definition);
               if (existing?.mode !== "constant" || existing.valueType !== definition.valueType || !arePLAElementTransformValuesEqual(existing.value, storedValue)) {
                  updates.set(element.id, {
                     ...element,
                     attributes: {
                        ...element.attributes,
                        [transformTargetAttributeId]: { mode: "constant", valueType: definition.valueType, value: storedValue }
                     }
                  });
               }
            } catch (error) {
               errors.push(`${element.name || element.id}: ${error instanceof Error ? error.message : String(error)}`);
            }
            if ((index + 1) % 20 === 0) {
               setTransformProgress(Localization.getText("Transform progress").replace("{processed}", String(index + 1)).replace("{total}", String(selectedElements.length)));
               await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
            }
         }

         let changed = 0;
         if (updates.size) {
            const nextElements = elements.map((element) => updates.get(element.id) || element);
            if (!validateNextState(nextElements, relations)) {
               updates.forEach((element) => errors.push(`${element.name || element.id}: ${Localization.getText("Transform save failed")}`));
            } else {
               const resolvedElements = resolveElementsForState(nextElements, relations);
               const elementsToSave = resolvedElements.filter((element) => updates.has(element.id));
               localPersistenceInProgress.current = true;
               try {
                  const service = dataService(selectedProject.esIndexName);
                  const saveResults = await Promise.allSettled(elementsToSave.map((element) => service.saveElement(element)));
                  const successfullySavedIds = new Set<string>();
                  const failedSaves: { element: PLAElementRecord; error: unknown }[] = [];
                  saveResults.forEach((saveResult, index) => {
                     const element = elementsToSave[index];
                     if (saveResult.status === "fulfilled") successfullySavedIds.add(element.id);
                     else failedSaves.push({ element, error: saveResult.reason });
                  });
                  changed = successfullySavedIds.size;
                  successfullySavedIds.forEach((elementId) => persistedElementIds.current.add(elementId));
                  const resolvedById = new Map(resolvedElements.map((element) => [element.id, element]));
                  setElements((previous) => previous.map((element) => successfullySavedIds.has(element.id) ? resolvedById.get(element.id) || element : element));
                  failedSaves.forEach(({ element, error }) => errors.push(`${element.name || element.id}: ${error instanceof Error ? error.message : String(error)}`));

                  const lockingError = failedSaves.find(({ error }) => error instanceof OptimisticLockingConflictError)?.error;
                  if (lockingError) await showSaveError(lockingError);
                  else if (failedSaves.length) appMessageDispatch(actionSetErrorMessage(Localization.getText("Transform save failed")));
               } finally {
                  localPersistenceInProgress.current = false;
               }
            }
         }
         setTransformResult({ processed: selectedElements.length, changed, unchanged: selectedElements.length - changed - errors.length, errors });
         setTransformProgress("");
      } finally {
         setTransformRunning(false);
      }
   };

   const handleClose = () => {
      removePLAMapLayers(mapFacade?.getMapPop?.());
      windowDispatch(closeWindow(WindowId.PLAWindow));
   };

   const saveElementLocal = async (element: PLAElementRecord) => {
      const exists = elements.some((item) => item.id === element.id);
      const nextElements = exists ? elements.map((item) => item.id === element.id ? element : item) : [...elements, element];
      if (await persistElementChanges(nextElements, relations, [element.id])) setShowElementEditor(undefined);
   };

   const deleteRelationPersisted = async (relationId: string) => {
      if (readOnly) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Editing elements requires write access")));
         return;
      }
      if (!selectedProject) return;
      const nextRelations = relations.filter((relation) => relation.id !== relationId);
      if (!validateNextState(elements, nextRelations)) return;
      localPersistenceInProgress.current = true;
      try {
         if (persistedRelationIds.current.has(relationId)) await dataService(selectedProject.esIndexName).deleteRelation(relationId);
         persistedRelationIds.current.delete(relationId);
         setRelations(nextRelations);
         setElements(resolveElementsForState(elements, nextRelations));
      } catch (error: any) {
         await showSaveError(error);
      } finally {
         localPersistenceInProgress.current = false;
      }
   };

   const cleanupStaleRelations = async () => {
      setConfirmRelationCleanupProjectId(undefined);
      if (!selectedProject || readOnly || relationCleanupRunning || localPersistenceInProgress.current ||
         confirmRelationCleanupProjectId !== selectedProject.id) return;
      setRelationCleanupRunning(true);
      localPersistenceInProgress.current = true;
      try {
         const project = await projectService().loadProject(selectedProject.id);
         if (!hasPLAWriteAccess(project, sessionState.userRef, sessionState.customerRef)) {
            throw new Error(Localization.getText("Stale relation cleanup requires write access"));
         }
         const result = await cleanupPLAStaleRelations(project, dataService(project.esIndexName));
         applyRemoteRecords(result.records);
         const summary = Localization.getText("Stale relation cleanup statistics")
            .replace("{project}", project.name)
            .replace("{scanned}", String(result.scanned))
            .replace("{stale}", String(result.stale))
            .replace("{deleted}", String(result.deleted))
            .replace("{orphanedElements}", String(result.orphanedElements))
            .replace("{deletedElements}", String(result.deletedElements))
            .replace("{errors}", String(result.errors.length));
         if (result.errors.length) {
            appMessageDispatch(actionSetErrorMessage(`${summary}\n${result.errors.map((error) => `${error.recordId}: ${Localization.getText(error.message)}`).join("\n")}`));
         } else {
            appMessageDispatch(actionSetInfoMessage(summary));
         }
      } catch (error) {
         appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to clean up stale relations")}: ${error instanceof Error ? error.message : String(error)}`));
      } finally {
         localPersistenceInProgress.current = false;
         setRelationCleanupRunning(false);
      }
   };

   const deleteElementLocal = async (element: PLAElementRecord) => {
      if (readOnly) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Editing elements requires write access")));
         return;
      }
      if (!selectedProject) return;
      const relationIdsToDelete = relations.filter((relation) => relation.fromId === element.id || relation.toId === element.id).map((relation) => relation.id);
      const nextElements = elements.filter((item) => item.id !== element.id);
      const nextRelations = relations.filter((relation) => relation.fromId !== element.id && relation.toId !== element.id);
      if (!validateNextState(nextElements, nextRelations)) return;
      localPersistenceInProgress.current = true;
      try {
         const service = dataService(selectedProject.esIndexName);
         await Promise.all([
            ...relationIdsToDelete.filter((relationId) => persistedRelationIds.current.has(relationId)).map((relationId) => service.deleteRelation(relationId)),
            ...(persistedElementIds.current.has(element.id) ? [service.deleteElement(element.id)] : [])
         ]);
         persistedElementIds.current.delete(element.id);
         relationIdsToDelete.forEach((relationId) => persistedRelationIds.current.delete(relationId));
         setElements(resolveElementsForState(nextElements, nextRelations));
         setRelations(nextRelations);
         setConfirmDeleteElement(undefined);
      } catch (error: any) {
         await showSaveError(error);
      } finally {
         localPersistenceInProgress.current = false;
      }
   };

   const selectElementTypeSelectionMode = (mode: ElementTypeMapSelectionMode) => {
      if (!selectedProject) {
         setElementTypeMapSelectionMode(mode);
         return;
      }
      const firstType = getSortedElementTypeIds(selectedProject)[0];
      const fallbackSelectedElementTypeId = lastSelectedElementTypeId && selectedProject.elementTypes[lastSelectedElementTypeId] ? lastSelectedElementTypeId : undefined;
      if (mode === "single") {
         const selectedElementTypeId = getLowestSortVisibleElementTypeId(selectedProject, visibleElementTypes) || fallbackSelectedElementTypeId || firstType;
         setLastSelectedElementTypeId(selectedElementTypeId);
         setVisibleElementTypes(singleElementTypeVisible(selectedProject, selectedElementTypeId));
      } else {
         const selectedElementTypeId = getLowestSortVisibleElementTypeId(selectedProject, visibleElementTypes) || fallbackSelectedElementTypeId || firstType;
         setLastSelectedElementTypeId(selectedElementTypeId);
         setVisibleElementTypes(singleElementTypeVisible(selectedProject, selectedElementTypeId));
      }
      setElementTypeMapSelectionMode(mode);
   };

   const selectVisibleElementType = (elementTypeId: string, checked: boolean) => {
      if (!selectedProject) return;
      if (elementTypeMapSelectionMode === "single") {
         setVisibleElementTypes(singleElementTypeVisible(selectedProject, elementTypeId));
         setLastSelectedElementTypeId(elementTypeId);
         return;
      }
      setVisibleElementTypes((prev) => ({ ...prev, [elementTypeId]: checked }));
      if (checked) setLastSelectedElementTypeId(elementTypeId);
   };

   const runOptimizer = async () => {
      if (!selectedProject || optimizerRunning) return;
      const targetAttributeId = optimizerTargetAttributeId || defaultOptimizerTargetAttributeId;
      if (!targetAttributeId) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No numeric project attributes")));
         return;
      }
      setShowOptimizerParameters(false);
      setOptimizerResult(undefined);
      setOptimizerProgress(Localization.getText("Preparing optimizer"));
      setOptimizerProgressPercent(0);
      setOptimizerRunning(true);
      try {
         const result = await createPLAOptimizationResultAsync(selectedProject, elements, relations, {
            targetAttributeId,
            goal: optimizerGoal,
            algorithm: optimizerAlgorithm,
            changeElementTypeName: "Anvendelse",
            changeAttributeName: "Etageareal",
            onProgress: (progress) => {
               setOptimizerProgress(progress.message);
               setOptimizerProgressPercent(progress.percent);
            }
         });
         setOptimizerResult(result);
      } catch (error: any) {
         appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to run optimizer")}: ${error.message || error}`));
      } finally {
         setOptimizerRunning(false);
         setOptimizerProgress("");
         setOptimizerProgressPercent(0);
      }
   };

   const getOptimizerResultTargetText = (result: PLAOptimizationResult) => optimizerTargetOptions.find((option) => option.value === result.parameters.targetAttributeId)?.text || result.parameters.targetAttributeId;

   const getOptimizerResultGoalText = (goal: PLAOptimizerGoal) => goal === "maximize" ? Localization.getText("Maximize value") : Localization.getText("Minimize value");

   const getOptimizerResultAlgorithmText = (algorithm: PLAOptimizerAlgorithm) => algorithm === "astar" ? Localization.getText("A* (optimized)") : Localization.getText("Greedy (fast)");

   const applyOptimizerProposal = async (proposal: PLAOptimizerProposal) => {
      if (!selectedProject) return;
      const changesByElementId = new Map(proposal.changes.map((change) => [change.anvendelseElementId, change]));
      const nextElements = elements.map((element) => {
         const change = changesByElementId.get(element.id);
         if (!change) return element;
         return {
            ...element,
            attributes: {
               ...element.attributes,
               [change.etagearealAttributeId]: { mode: "constant" as const, valueType: "number" as const, value: change.proposedEtageareal }
            }
         };
      });
      if (await persistElementChanges(nextElements, relations, Array.from(changesByElementId.keys()))) {
         setOptimizerResult(undefined);
         appMessageDispatch(actionSetInfoMessage(Localization.getText("Optimizer proposal applied")));
      }
   };

   const showJernbanebyenLogo = isJernbanebyenProject(selectedProject);

   const renderElementTypeTabTitle = (elementTypeId: string, elementType: PLAProject["elementTypes"][string]) => (
      <>
         <input
            type={elementTypeMapSelectionMode === "single" ? "radio" : "checkbox"}
            className="pla-tab__mapToggle"
            name="pla-map-element-type"
            checked={visibleElementTypes[elementTypeId] !== false}
            aria-label={Localization.getText("Show element type on map")}
            title={Localization.getText("Show element type on map")}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => selectVisibleElementType(elementTypeId, event.target.checked)}
         />
         <span className="pla-tab__swatch" style={{ background: elementType.color || "#2d8f85" }} />
         {elementType.name || elementTypeId}
         <PLAAlertMark messages={alertMessagesByElementType[elementTypeId] || []} />
      </>
   );

   return (
      <PLACubeViewContext.Provider value={cubeViewSettings}>
      <AdvancedDragModal
         title={Localization.getText("PlanIT")}
         window={props.managedWindow}
         onClose={handleClose}
         allowUserMiniMize={true}
         allowPopout={true}
         allowFullscreen={true}
         className="pla-window"
         PosDefault={{ top: "12px", left: "calc(100% - 12px)", transform: "translateX(-100%)", width: "1100px", height: "720px" }}
         PositionToSave={{ left: true, top: true, width: true, height: true }}
      >
         <ADModalBody>
            <div className="pla-workspace">
               <div className="pla-hero">
                  <div className="pla-hero__project-picker">
                     <label className="pla-hero__project-label" htmlFor="pla-project-select">{Localization.getText("Choose project")}</label>
                     <select
                        id="pla-project-select"
                        className="pla-select"
                        value={selectedProjectId}
                        disabled={relationCleanupRunning}
                        onChange={(event) => setSelectedProjectId(event.target.value)}
                     >
                        <option value="">{Localization.getText("Choose project")}</option>
                        {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                     </select>
                     <a
                        className="pla-hero__system-logo-link"
                        href={PLAMolbakLogo.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={PLAMolbakLogo.href}
                     >
                        <img className="pla-hero__system-logo" src={PLAMolbakLogo.src} alt={PLAMolbakLogo.alt} />
                     </a>
                  </div>
                  {selectedProject ? (
                     <div className="pla-hero__toolbar">
                        <div className="pla-hero__identity">
                           {showJernbanebyenLogo ? (
                              <a
                                 className="pla-hero__project-logo-link"
                                 href={PLAJernbanebyenLogo.href}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 title={PLAJernbanebyenLogo.href}
                              >
                                 <img
                                    className="pla-hero__project-logo"
                                    src={PLAJernbanebyenLogo.src}
                                    alt={PLAJernbanebyenLogo.alt}
                                 />
                              </a>
                           ) : null}
                           <div className="pla-hero__identity-text">
                              <h2 className="pla-hero__name">{selectedProject.name}</h2>
                              {selectedProject.description ? <p className="pla-hero__description">{selectedProject.description}</p> : null}
                           </div>
                        </div>
                        <div className="pla-hero__actions">
                           <GlassButton className="pla-btn pla-btn--ghost" title={Localization.getText("PLA settings")} onClick={() => setShowMapSettings(true)}><MIBIcon icon="settings" /></GlassButton>
                           <PLAFilterButton className={`pla-btn pla-btn--ghost${cubeViewSettings.memberId || cubeViewSettings.latest ? " pla-btn--filter-active" : ""}`} disabled={!phaseDimensionId} title={Localization.getText("Global cube filter")} active={Boolean(cubeViewSettings.memberId || cubeViewSettings.latest)} onClick={() => setShowGlobalCubeFilter(true)} onClear={() => persistCubeViewSettings({ ...storedCubeViewSettings, memberId: undefined, latest: false })} />
                           <GlassButton className="pla-btn pla-btn--ghost" onClick={() => undefined}>{Localization.getText("Change log")}</GlassButton>
                           <GlassButton className="pla-btn pla-btn--ghost" onClick={() => undefined}>{Localization.getText("Versionering")}</GlassButton>
                           <PLAReportsMenu
                              label="Reports"
                              items={[
                                 { value: "freeArea", text: Localization.getText("Friarealsopgørelse") },
                              ]}
                              onSelect={() => {
                                 createPLAFreeAreaReport(selectedProject, elements, relations, sessionState.userName || sessionState.userRef || "")
                                    .catch((error) => appMessageDispatch(actionSetErrorMessage(`${Localization.getText("Failed to create report")}: ${error.message || error}`)));
                              }}
                           />
                           {administrator && <PLAReportsMenu
                              label="Tools"
                              items={[
                                 { value: "transformElements", text: Localization.getText("Transform elements"), disabled: readOnly || !transformElementTypeId || !transformTargetOptions.length },
                                 { value: "alerts", text: Localization.getText("Configured alerts"), disabled: !selectedProject },
                                 { value: "attributeUsage", text: Localization.getText("Attribute usage"), disabled: !selectedProject },
                                 { value: "cleanupRelations", text: Localization.getText(relationCleanupRunning ? "Cleaning up stale relations" : "Clean up stale relations"), disabled: readOnly || !selectedProject || loading || relationCleanupRunning || transformRunning || optimizerRunning },
                                 { value: "elementTree", text: Localization.getText("Element tree"), disabled: !elementTreeWindowParams },
                                 { value: "optimize", text: optimizerRunning ? Localization.getText("Optimizer running") : Localization.getText("Optimize"), disabled: optimizerRunning },
                                 { value: "llmCheck", text: Localization.getText("AI check"), disabled: !selectedProject },
                              ]}
                              onSelect={(value) => {
                                 if (value === "transformElements" && transformElementTypeId && transformTargetOptions.length) {
                                    setTransformTargetAttributeId((current) => transformTargetOptions.some((option) => option.value === current) ? current : transformTargetOptions[0].value);
                                    setTransformExpression("");
                                    setTransformResult(undefined);
                                    setShowElementTransform(true);
                                 } else if (value === "alerts" && selectedProject) windowDispatch(openWindow(WindowId.PLAConfiguredAlerts, { project: selectedProject }));
                                 else if (value === "attributeUsage" && selectedProject) windowDispatch(openWindow(WindowId.PLAAttributeUsage, { project: selectedProject, elements }));
                                 else if (value === "cleanupRelations" && selectedProject && !readOnly) setConfirmRelationCleanupProjectId(selectedProject.id);
                                 else if (value === "elementTree" && elementTreeWindowParams) windowDispatch(openWindow(WindowId.PLAElementTreeDiagram, elementTreeWindowParams));
                                 else if (value === "optimize") setShowOptimizerParameters(true);
                                 else if (value === "llmCheck") setShowLlmCheck(true);
                              }}
                           />}
                        </div>
                     </div>
                  ) : null}
               </div>
               {loading ? <div className="pla-boot"><Spinner /></div> : null}
               {optimizerRunning ? (
                  <div className="pla-progress">
                     <Spinner animation="border" size="sm" />
                     <span>{optimizerProgress || Localization.getText("Optimizer running")}</span>
                     <ProgressBar now={optimizerProgressPercent} />
                  </div>
               ) : null}
               {selectedProject ? (
                  <>
                     <div className="pla-tabs" role="tablist">
                        {sortedElementTypes.map(([elementTypeId, elementType]) => (
                           <div
                              key={elementTypeId}
                              role="tab"
                              tabIndex={0}
                              aria-selected={visibleActiveTab === elementTypeId}
                              className={`pla-tab${visibleActiveTab === elementTypeId ? " pla-tab--active" : ""}`}
                              onClick={() => setActiveTab(elementTypeId)}
                              onKeyDown={(event) => {
                                 if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    setActiveTab(elementTypeId);
                                 }
                              }}
                           >
                              {renderElementTypeTabTitle(elementTypeId, elementType)}
                           </div>
                        ))}
                     </div>
                     {sortedElementTypes.map(([elementTypeId, elementType]) => (
                        <div
                           key={elementTypeId}
                           role="tabpanel"
                           className={`pla-tab-panel${visibleActiveTab === elementTypeId ? " pla-tab-panel--active" : ""}`}
                        >
                           <PLATable
                              ref={(ref) => { tableRefs.current[elementTypeId] = ref; }}
                              project={selectedProject}
                              elementTypeId={elementTypeId}
                              elementType={elementType}
                              elements={elements.filter((element) => element.elementType === elementTypeId)}
                              allElements={elements}
                              relations={relations}
                              selectedElementId={selectedElementId}
                              readOnly={readOnly}
                              onRowClick={zoomToElement}
                              onAddElement={() => setShowElementEditor(createEmptyPLAElement(selectedProject, elementTypeId))}
                              onOpenElement={setShowElementEditor}
                              onCreateRelation={setShowRelationEditor}
                              onDeleteElement={setConfirmDeleteElement}
                           />
                        </div>
                     ))}
                  </>
               ) : null}
            </div>
         </ADModalBody>
         {showGlobalCubeFilter && selectedProject && phaseDimensionId ? createPortal(
            <PLACubeGlobalFilterDialog
               dimensionName={selectedProject.elementTypes[phaseDimensionId].name}
               phases={phases}
               settings={cubeViewSettings}
               onChange={changeCubeViewSettings}
               onClose={() => setShowGlobalCubeFilter(false)}
            />, plaPortalRoot
         ) : null}
         {showElementEditor && selectedProject ? createPortal(
            <PLAElementDialog
               project={selectedProject}
               element={showElementEditor}
               elements={elements}
               relations={relations}
               availableLayers={availableLayers}
               readOnly={readOnly}
               showFormulae={showFormulae}
               onClose={() => setShowElementEditor(undefined)}
               onSave={saveElementLocal}
               onCreateRelation={setShowRelationEditor}
               onDeleteRelation={deleteRelationPersisted}
            />,
            plaPortalRoot
         ) : null}
         {showRelationEditor && selectedProject ? createPortal(
            <PLARelationDialog
               project={selectedProject}
               sourceElement={showRelationEditor}
               elements={elements}
               onClose={() => setShowRelationEditor(undefined)}
               onSave={async (relation, newElement) => {
                  if (readOnly) {
                     appMessageDispatch(actionSetErrorMessage(Localization.getText("Editing elements requires write access")));
                     return;
                  }
                  const nextRelations = [...relations, relation];
                  const nextElements = newElement ? [...elements, newElement] : elements;
                  if (!validateNextState(nextElements, nextRelations)) return;
                  const resolvedElements = resolveElementsForState(nextElements, nextRelations);
                  try {
                     const service = dataService(selectedProject.esIndexName);
                     if (newElement) await service.saveElement(resolvedElements.find((element) => element.id === newElement.id) || newElement);
                     await service.saveRelation(relation);
                     if (newElement) persistedElementIds.current.add(newElement.id);
                     persistedRelationIds.current.add(relation.id);
                     setRelations(nextRelations);
                     setElements(resolvedElements);
                     setShowRelationEditor(undefined);
                  } catch (error: any) {
                     showSaveError(error);
                  }
               }}
            />,
            plaPortalRoot
         ) : null}
         {showElementTransform && selectedProject && transformElementTypeId ? createPortal(
            <AdvancedDragModal
               title={Localization.getText("Transform elements")}
               PosDefault={{ width: "800px", height: "620px", left: "50%", transform: "translateX(-50%)", top: "70px" }}
               PositionToSave={{ left: true, top: true, width: true, height: true }}
               saveKey="PLAElementTransform"
               onClose={() => {
                  if (!transformRunning) {
                     setShowElementTransform(false);
                     setTransformExpressionEditorOpen(false);
                  }
               }}
            >
               <ADModalBody>
                  <div className="pla-element-transform">
                     <p>{Localization.getText("Transform elements hint")
                        .replace("{elementType}", selectedProject.elementTypes[transformElementTypeId]?.name || transformElementTypeId)
                        .replace("{count}", String(elements.filter((element) => element.elementType === transformElementTypeId).length))}</p>
                     <label>
                        <span>{Localization.getText("Result attribute")}</span>
                        <select value={transformTargetAttributeId} disabled={transformRunning} onChange={(event) => { setTransformTargetAttributeId(event.target.value); setTransformResult(undefined); }}>
                           {transformTargetOptions.map((option) => <option key={option.value} value={option.value}>{option.text}</option>)}
                        </select>
                     </label>
                     <fieldset disabled={transformRunning} style={{ minWidth: 0, margin: 0, padding: 0, border: 0 }}>
                        <PLAExpressionInput
                           value={transformExpression}
                           onChange={(value) => { setTransformExpression(value); setTransformResult(undefined); }}
                           project={selectedProject}
                           elementTypeId={transformElementTypeId}
                           elements={elements}
                        />
                     </fieldset>
                     <GlassButtonRow>
                        <GlassButton disabled={transformRunning} onClick={() => setTransformExpressionEditorOpen(true)}>{Localization.getText("Open expression editor")}</GlassButton>
                        <GlassButton disabled={transformRunning || !transformExpression.trim() || !validatePLAExpression(transformExpression, { project: selectedProject, elementTypeId: transformElementTypeId }).valid} onClick={() => void runElementTransform()}>
                           {Localization.getText(transformRunning ? "Transform running" : "Transform")}
                        </GlassButton>
                        {transformRunning ? <span>{transformProgress || Localization.getText("Transform running")}</span> : null}
                     </GlassButtonRow>
                     {transformResult ? (
                        <section className="pla-element-transform__results" aria-live="polite">
                           <h3>{Localization.getText("Transform results")}</h3>
                           <dl>
                              <dt>{Localization.getText("Processed")}</dt><dd>{transformResult.processed}</dd>
                              <dt>{Localization.getText("Changed")}</dt><dd>{transformResult.changed}</dd>
                              <dt>{Localization.getText("Unchanged")}</dt><dd>{transformResult.unchanged}</dd>
                              <dt>{Localization.getText("Errors")}</dt><dd>{transformResult.errors.length}</dd>
                           </dl>
                           {transformResult.errors.length ? <ul>{transformResult.errors.map((error, index) => <li key={`${index}-${error}`}>{error}</li>)}</ul> : null}
                        </section>
                     ) : null}
                  </div>
               </ADModalBody>
               <ADModalFooter>
                  <GlassButtonRow adjust="end">
                     <GlassButton disabled={transformRunning} onClick={() => { setShowElementTransform(false); setTransformExpressionEditorOpen(false); }}>{Localization.getText("Close")}</GlassButton>
                  </GlassButtonRow>
               </ADModalFooter>
               {transformExpressionEditorOpen ? <PLAExpressionSimulationDialog
                  project={selectedProject}
                  elementTypeId={transformElementTypeId}
                  element={elements.find((element) => element.elementType === transformElementTypeId)}
                  elements={elements}
                  relations={relations}
                  portalRoot={plaPortalRoot}
                  initialExpression={transformExpression}
                  onClose={() => setTransformExpressionEditorOpen(false)}
                  onApply={(expression) => { setTransformExpression(expression); setTransformResult(undefined); setTransformExpressionEditorOpen(false); }}
               /> : null}
            </AdvancedDragModal>,
            plaPortalRoot
         ) : null}
         {confirmRelationCleanupProjectId && selectedProject?.id === confirmRelationCleanupProjectId ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Clean up stale relations")} submitLabel="Clean up stale relations" onSubmit={cleanupStaleRelations} onCancel={() => setConfirmRelationCleanupProjectId(undefined)}>
               {Localization.getText("Confirm stale relation cleanup").replace("{project}", selectedProject.name)}
            </ConfirmationDialog>,
            plaPortalRoot
         ) : null}
         {confirmDeleteElement ? createPortal(
            <ConfirmationDialog showWindow={1} header={Localization.getText("Confirm deletion")} onSubmit={() => deleteElementLocal(confirmDeleteElement)} onCancel={() => setConfirmDeleteElement(undefined)}>
               {Localization.getText("Confirm delete of element {elementId}?").replace("{elementId}", confirmDeleteElement.name)}
            </ConfirmationDialog>,
            plaPortalRoot
         ) : null}
         {showLlmCheck && selectedProject ? createPortal(
            <PLALLMCheckDialog
               project={selectedProject}
               elements={elements}
               relations={relations}
               readOnly={readOnly}
               caller={{
                  userRef: sessionState.userRef || "",
                  userName: sessionState.userName || sessionState.userRef || "",
                  organizationId: sessionState.customerRef || "",
               }}
               onClose={() => setShowLlmCheck(false)}
               onApplyValues={applyLlmCheckValues}
            />,
            plaPortalRoot
         ) : null}
         {showMapSettings ? createPortal(
            <AdvancedDragModal title={Localization.getText("PLA settings")} PosDefault={{ width: "400px", height: "340px", left: "50%", transform: "translateX(-50%)", top: "90px" }} onClose={() => setShowMapSettings(false)}>
               <ADModalBody>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                     <label style={{ display: "inline-flex", alignItems: "center", gap: 6, margin: 0 }}>
                        <input type="checkbox" checked={elementTypeMapSelectionMode === "single"} onChange={(event) => selectElementTypeSelectionMode(event.target.checked ? "single" : "multiple")} />
                        {Localization.getText("Single element type on map")}
                     </label>
                     <label style={{ display: "inline-flex", alignItems: "center", gap: 6, margin: 0 }}>
                        <input type="checkbox" checked={showPolygonNames} onChange={(event) => setShowPolygonNames(event.target.checked)} />
                        {Localization.getText("Show names on polygons")}
                     </label>
                     <label style={{ display: "inline-flex", alignItems: "center", gap: 6, margin: 0 }}>
                        <input type="checkbox" checked={showFormulae} onChange={(event) => setShowFormulae(event.target.checked)} />
                        {Localization.getText("Show formulae")}
                     </label>
                  </div>
               </ADModalBody>
               <ADModalFooter>
                  <GlassButtonRow adjust="end">
                     <GlassButton onClick={() => setShowMapSettings(false)}>{Localization.getText("Close")}</GlassButton>
                  </GlassButtonRow>
               </ADModalFooter>
            </AdvancedDragModal>,
            plaPortalRoot
         ) : null}
         {showOptimizerParameters && selectedProject ? createPortal(
            <AdvancedDragModal title={Localization.getText("Optimizer parameters")} PosDefault={{ width: "520px", height: "500px", left: "50%", transform: "translateX(-50%)", top: "80px" }} onClose={() => setShowOptimizerParameters(false)}>
               <ADModalBody>
                  <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                     <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <h4 style={{ margin: 0 }}>{Localization.getText("Optimize this value")}</h4>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                           <span>{Localization.getText("Attribute to optimize")}</span>
                           <select value={optimizerTargetAttributeId || defaultOptimizerTargetAttributeId} onChange={(event) => setOptimizerTargetAttributeId(event.target.value)}>
                              {optimizerTargetOptions.map((option) => <option key={option.value} value={option.value}>{option.text}</option>)}
                           </select>
                        </label>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                           <span>{Localization.getText("Optimization goal")}</span>
                           <select value={optimizerGoal} onChange={(event) => setOptimizerGoal(event.target.value as PLAOptimizerGoal)}>
                              <option value="minimize">{Localization.getText("Minimize value")}</option>
                              <option value="maximize">{Localization.getText("Maximize value")}</option>
                           </select>
                        </label>
                     </section>
                     <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <h4 style={{ margin: 0 }}>{Localization.getText("By changing")}</h4>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                           <span>{Localization.getText("Attribute type to change")}</span>
                           <select value="Anvendelse" disabled><option value="Anvendelse">Anvendelse</option></select>
                        </label>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                           <span>{Localization.getText("Attribute to change")}</span>
                           <select value="Etageareal" disabled><option value="Etageareal">Etageareal</option></select>
                        </label>
                     </section>
                     <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        <h4 style={{ margin: 0 }}>{Localization.getText("Optimizer section settings")}</h4>
                        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                           <span>{Localization.getText("Optimizer algorithm")}</span>
                           <select value={optimizerAlgorithm} onChange={(event) => setOptimizerAlgorithm(event.target.value as PLAOptimizerAlgorithm)}>
                              <option value="greedy">{Localization.getText("Greedy (fast)")}</option>
                              <option value="astar">{Localization.getText("A* (optimized)")}</option>
                           </select>
                        </label>
                     </section>
                  </div>
               </ADModalBody>
               <ADModalFooter>
                  <GlassButtonRow>
                     <GlassButton disabled={!optimizerTargetOptions.length || optimizerRunning} onClick={runOptimizer}>{Localization.getText("Start optimization")}</GlassButton>
                     <GlassButton onClick={() => setShowOptimizerParameters(false)}>{Localization.getText("Cancel optimization")}</GlassButton>
                  </GlassButtonRow>
               </ADModalFooter>
            </AdvancedDragModal>,
            plaPortalRoot
         ) : null}
         {optimizerResult ? createPortal(
            <AdvancedDragModal title={Localization.getText("PLA optimizer proposals")} PosDefault={{ width: "760px", height: "560px", left: "50%", transform: "translateX(-50%)", top: "90px" }} onClose={() => setOptimizerResult(undefined)}>
               <ADModalBody>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8, marginBottom: 12, padding: 10, border: "1px solid rgba(255,255,255,.25)", borderRadius: 4 }}>
                     <strong style={{ gridColumn: "1 / -1" }}>{Localization.getText("Optimization input parameters")}</strong>
                     <span>{Localization.getText("Attribute to optimize")}: {getOptimizerResultTargetText(optimizerResult)}</span>
                     <span>{Localization.getText("Optimization goal")}: {getOptimizerResultGoalText(optimizerResult.parameters.goal)}</span>
                     <span>{Localization.getText("Optimizer algorithm")}: {getOptimizerResultAlgorithmText(optimizerResult.parameters.algorithm)}</span>
                     <span>{Localization.getText("Attribute type to change")}: {optimizerResult.parameters.changeElementTypeName}</span>
                     <span>{Localization.getText("Attribute to change")}: {optimizerResult.parameters.changeAttributeName}</span>
                  </div>
                  {optimizerResult.proposals.length === 0 ? (
                     <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <div>{Localization.getText("No optimizer proposals")}</div>
                        {optimizerResult.warningLimitStats.length ? (
                           <div>
                              <h4 style={{ margin: "4px 0" }}>{Localization.getText("Warnings limiting optimizer")}</h4>
                              <table style={{ width: "100%" }}>
                                 <thead>
                                    <tr>
                                       <th style={{ textAlign: "left" }}>{Localization.getText("Warning")}</th>
                                       <th style={{ textAlign: "left" }}>{Localization.getText("Blocked proposals")}</th>
                                       <th style={{ textAlign: "left" }}>{Localization.getText("Examples")}</th>
                                    </tr>
                                 </thead>
                                 <tbody>
                                    {optimizerResult.warningLimitStats.map((stat) => (
                                       <tr key={stat.id}>
                                          <td>{stat.message}</td>
                                          <td>{stat.count}</td>
                                          <td>{stat.examples.join(", ")}</td>
                                       </tr>
                                    ))}
                                 </tbody>
                              </table>
                           </div>
                        ) : null}
                     </div>
                  ) : (
                     <table style={{ width: "100%" }}>
                        <thead>
                           <tr>
                              <th style={{ textAlign: "left" }}>{Localization.getText("Top-level element")}</th>
                              <th style={{ textAlign: "left" }}>{Localization.getText("Surplus value")}</th>
                              <th style={{ textAlign: "left" }}>{Localization.getText("Changes")}</th>
                              <th style={{ textAlign: "left" }}>{Localization.getText("Proposal")}</th>
                              <th style={{ textAlign: "left" }}>{Localization.getText("Actions")}</th>
                           </tr>
                        </thead>
                        <tbody>
                           {optimizerResult.proposals.map((proposal) => (
                              <tr key={proposal.id}>
                                 <td>{proposal.topElementName}</td>
                                 <td>{formatPLANumber(proposal.currentValue)}</td>
                                 <td>
                                    <table style={{ width: "100%" }}>
                                       <thead>
                                          <tr>
                                             <th style={{ textAlign: "left" }}>{Localization.getText("Byggefelt")}</th>
                                             <th style={{ textAlign: "left" }}>{Localization.getText("Anvendelse")}</th>
                                             <th style={{ textAlign: "left" }}>{Localization.getText("Current Etageareal")}</th>
                                             <th style={{ textAlign: "left" }}>{Localization.getText("Proposed Etageareal")}</th>
                                             <th style={{ textAlign: "left" }}>{Localization.getText("Change")}</th>
                                          </tr>
                                       </thead>
                                       <tbody>
                                          {proposal.changes.map((change) => (
                                             <tr key={change.id}>
                                                <td>{change.byggefeltName || ""}</td>
                                                <td>{change.anvendelseName}</td>
                                                <td>{formatPLANumber(change.currentEtageareal)}</td>
                                                <td>{formatPLANumber(change.proposedEtageareal)}</td>
                                                <td>{formatPLANumber(change.etagearealChange)}</td>
                                             </tr>
                                          ))}
                                       </tbody>
                                    </table>
                                 </td>
                                 <td>
                                    <strong>{proposal.actionText}</strong><br />
                                    <span>{proposal.details}</span>
                                    {proposal.expressionTrace.length ? <details><summary>{Localization.getText("Expression trace")}</summary><ul>{proposal.expressionTrace.map((line) => <li key={line}>{line}</li>)}</ul></details> : null}
                                 </td>
                                 <td><GlassButton onClick={() => applyOptimizerProposal(proposal)}>{Localization.getText("Do it")}</GlassButton></td>
                              </tr>
                           ))}
                        </tbody>
                     </table>
                  )}
               </ADModalBody>
               <ADModalFooter>
                  <GlassButtonRow>
                     <GlassButton onClick={() => setOptimizerResult(undefined)}>{Localization.getText("Close")}</GlassButton>
                  </GlassButtonRow>
               </ADModalFooter>
            </AdvancedDragModal>,
            plaPortalRoot
         ) : null}
      </AdvancedDragModal>
      </PLACubeViewContext.Provider>
   );
}

export type PLAElementTreeDiagramWindowParams = {
   project: PLAProject;
   elements: PLAElementRecord[];
   relations: PLARelationRecord[];
   onElementClick: (elementId: string) => void;
};

export function PLAElementTreeDiagramWindow(props: { managedWindow: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const params = props.managedWindow.windowParams as PLAElementTreeDiagramWindowParams | undefined;

   if (!params) return null;

   return (
      <AdvancedDragModal
         title={Localization.getText("Element tree")}
         window={props.managedWindow}
         allowUserMiniMize={true}
         allowPopout={true}
         allowFullscreen={true}
         PosDefault={{ width: "980px", height: "680px", left: "50%", transform: "translateX(-50%)", top: "40px" }}
         PositionToSave={{ left: true, top: true, width: true, height: true }}
         saveKey="PLAElementTreeDiagram"
         onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
      >
         <ADModalBody>
            <PLAElementTreeDiagram project={params.project} elements={params.elements} relations={params.relations} onElementClick={params.onElementClick} />
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow adjust="end">
               <GlassButton onClick={() => windowDispatch(closeWindow(props.managedWindow.id))}>{Localization.getText("Close")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}

export type PLAConfiguredAlertsWindowParams = {
   project: PLAProject;
};

export function PLAConfiguredAlertsWindow(props: { managedWindow: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const params = props.managedWindow.windowParams as PLAConfiguredAlertsWindowParams | undefined;
   if (!params) return null;

   const groups = Object.entries(params.project.elementTypes || {})
      .sort(([, a], [, b]) => a.sortOrder - b.sortOrder)
      .flatMap(([elementTypeId, elementType]) => {
         const attributes = Object.entries(elementType.attributes || {})
            .sort(([, a], [, b]) => (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER))
            .flatMap(([attributeId, attribute]) => {
               const alerts = Object.entries(attribute.alerts || {}).sort(([a], [b]) => a.localeCompare(b));
               return alerts.length ? [{
                  attributeName: attribute.name || attributeId,
                  alerts: alerts.map(([, alert]) => alert)
               }] : [];
            });
         return attributes.length ? [{ elementTypeName: elementType.name || elementTypeId, attributes }] : [];
      });

   return (
      <AdvancedDragModal
         title={Localization.getText("Configured alerts")}
         window={props.managedWindow}
         allowUserMiniMize={true}
         PosDefault={{ width: "900px", height: "560px", left: "50%", transform: "translateX(-50%)", top: "80px" }}
         PositionToSave={{ left: true, top: true, width: true, height: true }}
         saveKey="PLAConfiguredAlerts"
         onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
      >
         <ADModalBody>
            <div className="pla-configured-alerts">
               <table>
                  <thead>
                     <tr>
                        <th>{Localization.getText("Element type")}</th>
                        <th>{Localization.getText("Attribute")}</th>
                        <th>{Localization.getText("Expression")}</th>
                        <th>{Localization.getText("Message")}</th>
                        <th>{Localization.getText("Optimizer may override")}</th>
                     </tr>
                  </thead>
                  <tbody>
                     {groups.length ? groups.map((group) => {
                        const elementTypeRowCount = group.attributes.reduce((count, attribute) => count + attribute.alerts.length, 0);
                        let isFirstElementTypeRow = true;
                        return group.attributes.map((attribute) => attribute.alerts.map((alert, index) => {
                           const isFirstAttributeRow = index === 0;
                           const showElementType = isFirstElementTypeRow;
                           isFirstElementTypeRow = false;
                           return (
                              <tr key={`${group.elementTypeName}-${attribute.attributeName}-${index}`}>
                                 {showElementType ? <th scope="rowgroup" rowSpan={elementTypeRowCount}>{group.elementTypeName}</th> : null}
                                 {isFirstAttributeRow ? <th scope="rowgroup" rowSpan={attribute.alerts.length}>{attribute.attributeName}</th> : null}
                                 <td><code>{alert.booleanExpression}</code></td>
                                 <td>{alert.alertMessage}</td>
                                 <td>{Localization.getText(alert.optimizerMayOverride ? "yes" : "No")}</td>
                              </tr>
                           );
                        }));
                     }) : (
                        <tr><td colSpan={5}>{Localization.getText("No configured alerts")}</td></tr>
                     )}
                  </tbody>
               </table>
            </div>
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow adjust="end">
               <GlassButton onClick={() => windowDispatch(closeWindow(props.managedWindow.id))}>{Localization.getText("Close")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}
